import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Screenshot,
  Section,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const FindingYourWay = () => (
  <Page
    title="Finding Your Way Around"
    intro="AmbuNet has a lot of pages, but you should never need to hunt for
    one. This guide covers the tools that get you anywhere fast: the global
    search, the sidebar, the Quick Add menu, contextual help, notifications,
    favourites, the sitemap, and choosing your own landing page."
  >
    <Section title="Search everything with ⌘K">
      <p>
        Press <strong>⌘K</strong> (or <strong>Ctrl+K</strong> on Windows)
        anywhere in AmbuNet — or use the search button at the top of the
        sidebar on a phone — to open the global search. Start typing and
        results appear grouped by kind:
      </p>
      <Bullets>
        <Bullet>
          <strong>Pages</strong> — every AmbuNet page you have access to, so
          "overtime" takes you straight to Overtime Claims.
        </Bullet>
        <Bullet>
          <strong>Create</strong> — the same create actions as the header's
          Quick Add menu, so typing "incident" offers Report an Incident.
        </Bullet>
        <Bullet>
          <strong>Knowledgebase</strong> — articles from this documentation,
          opened in the help site.
        </Bullet>
        <Bullet>
          <strong>People</strong> — active staff by name. Everyone can find a
          name; the result only links through to the employee record for
          Personnel and Manager team members.
        </Bullet>
        <Bullet>
          <strong>Vehicles</strong> — by registration or call sign, for the
          teams that can open the fleet pages (Fleet, Make Ready, Manager,
          Supervisor).
        </Bullet>
        <Bullet>
          <strong>Events</strong> — by title, for Event Planning, Manager, and
          Supervisor team members.
        </Bullet>
        <Bullet>
          <strong>Patient Transport</strong> — bookings by reference and
          patients by name, for PTS Co-Ordinators only. Because these results
          are patient-identifiable, they also need a recent MFA security check
          — without one, the search shows a locked note instead of results.
        </Bullet>
      </Bullets>
      <Screenshot url="getting-started/global-search.png">
        The ⌘K global search with results grouped into people, vehicle, and
        event matches.
      </Screenshot>
      <p>
        With nothing typed, the dialog shows your <strong>Recent</strong> pages
        and <strong>Favourites</strong> instead, so a quick ⌘K + Enter bounces
        you back to wherever you were working.
      </p>
      <Info title="Results respect permissions">
        Every entity group is checked on the server against the same rules as
        the page it links to — two people typing the same thing can see quite
        different results.
      </Info>
    </Section>

    <Section title="The sidebar">
      <p>
        The sidebar groups pages into sections — My Work, Workforce, People,
        Command &amp; Control, Events, Patient Transport, Fleet &amp;
        Equipment, Patient Records, Governance, Customers, Finance, System,
        and Help — and only shows the entries your teams and roles allow.
      </p>
      <Bullets>
        <Bullet>
          <strong>Collapse the sections you don't use</strong> — click a
          section header to fold it away. Which sections are open is saved to
          your account, so the layout follows you across devices and reloads.
        </Bullet>
        <Bullet>
          <strong>Numeric badges</strong> show live counts of work waiting
          behind an entry: your open tasks, shift applications, uncovered
          shifts, overtime claims, new incidents, new patient feedback, Speak
          Up concerns, open defects, pending client accounts, and unread Crew
          Vault items. Each badge only appears for the people who can see the
          page it sits on.
        </Bullet>
        <Bullet>
          <strong>Favourites</strong> — pages you've pinned (see the
          favourites bar below) are listed near the top of the sidebar for
          one-click access.
        </Bullet>
        <Bullet>
          <strong>Recent</strong> — your last few visited pages, so bouncing
          between two screens never means re-finding them.
        </Bullet>
        <Bullet>
          <strong>Governance is split into sub-groups</strong> — For Everyone
          (announcements, Crew Vault, My Training, medicine administration,
          Speak Up, PGDs, policies, SOPs), Quality &amp; Safety, Medicines,
          Compliance &amp; CQC, and Meetings — so everyday crew pages aren't
          buried among management tools.
        </Bullet>
      </Bullets>
      <Screenshot url="getting-started/sidebar-navigation.png">
        The sidebar with its grouped sections, live badges, and the Favourites
        and Recent blocks near the top.
      </Screenshot>
      <Info title="My Training moved">
        My Training now lives under <strong>Governance → For Everyone</strong>{' '}
        rather than in a training section of its own.
      </Info>
    </Section>

    <Section title="Quick Add — the + menu">
      <p>
        The <strong>+</strong> button in the header is the fastest way to
        create something from anywhere: a task, an event, a PTS booking, an
        ePCR, an incident, a Speak Up concern, a defect, a start-of-shift
        check, a vehicle clean, a fuel record, or an announcement. Like
        everything else, the menu only lists the actions your role allows —
        and the same actions appear in ⌘K search under <strong>Create</strong>.
      </p>
    </Section>

    <Section title="Contextual help — the ? button">
      <p>
        The <strong>?</strong> button in the header opens a help drawer for the
        page you're actually on: the matching knowledgebase article (and a
        how-to video where one exists), with a button to open the full article.
        Pages without a dedicated article get suggested articles instead. It's
        the quickest route into this documentation when you're stuck
        mid-task.
      </p>
      <Screenshot url="getting-started/help-drawer.png">
        The ? help drawer showing the knowledgebase guide for the page you're
        on.
      </Screenshot>
    </Section>

    <Section title="Notifications">
      <p>
        The bell in the header shows your latest notifications — the same inbox
        the mobile app reads. Open one to jump to the item it's about, use{' '}
        <strong>Mark all read</strong> to clear the badge, or choose{' '}
        <strong>View all notifications</strong> to open the{' '}
        <DocLink text="Notifications" url="/notifications" /> page: your full
        paginated notification history, with unread counts and mark-as-read
        controls. How you receive each kind of notification (email, app, or
        neither) is set per category in My Account.
      </p>
      <Screenshot url="getting-started/notifications-page.png">
        The Notifications page with your full notification history and
        mark-as-read controls.
      </Screenshot>
    </Section>

    <Section title="Favourites bar">
      <p>
        Under the header sits a favourites bar. On any page, click the star to
        pin the current page as a chip — give it your own label if the
        suggested one isn't right. Pinned pages appear as chips in the bar and
        in the sidebar's Favourites block, and favourites can point at
        anything, including a filtered list or a specific record you keep
        coming back to. Remove a chip when you're done with it.
      </p>
    </Section>

    <Section title="The sitemap">
      <p>
        <DocLink text="Sitemap" url="/sitemap" /> lists every page you can
        access, grouped by section — useful when you know a page exists but
        can't remember what it's called, and a handy way to discover what your
        roles unlock.
      </p>
      <Screenshot url="getting-started/sitemap.png">
        The sitemap listing every page your account can access, grouped by
        section.
      </Screenshot>
    </Section>

    <Section title="Choose your own landing page">
      <p>
        If your day always starts on the same screen — Global Roster, the PTS
        planner, the Defects list — set it as your{' '}
        <strong>default landing page</strong> under{' '}
        <DocLink text="My Account" url="/my-account" /> →{' '}
        <strong>Preferences</strong>. The first time you open AmbuNet in a
        browser session you're taken straight there instead of the Home
        Screen; visiting Home afterwards works as normal, so the preference
        changes where you land, not what you can see.
      </p>
      <Screenshot url="getting-started/my-account-preferences.png">
        The My Account Preferences tab with the default landing page picker.
      </Screenshot>
    </Section>

    <Section title="Where to go next">
      <p>
        For what the Home Screen itself shows — the To Do bar, clocking in and
        out, Quick Links, and Team Actions — see{' '}
        <DocLink
          docsPage
          url="/getting-started/home-screen"
          text="Home Screen"
        />
        . For who can see what, see{' '}
        <DocLink
          docsPage
          url="/getting-started/permissions"
          text="Permissions &amp; Roles"
        />
        .
      </p>
    </Section>
  </Page>
)

export default FindingYourWay
